import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useFleetPermissions } from "@/hooks/use-fleet-permissions";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FuelLogFormSheet, FuelLogFormData } from "@/components/fleet/FuelLogFormSheet";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DataTable } from "@/components/admin/data-table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Fuel, Car, Calculator, Plus, Edit2, Trash2, Calendar, FileText, MapPin, Camera, Smartphone, Eye } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ColumnDef } from "@tanstack/react-table";
import { FileUploadZone } from "@/components/fleet/FileUploadZone";
import { LocationDisplay } from "@/components/fleet/LocationDisplay";
import FuelReview from "./FuelReview";

interface FuelLog {
  id: string;
  vehicle_id: string;
  driver_id?: string;
  station_id?: string;
  occurred_at: string;
  liters: number;
  price_unit: number | null;
  total_value: number | null;
  odometer: number | null;
  fuel_type: string;
  invoice_path?: string;
  odometer_image_path?: string;
  pump_image_path?: string;
  invoice_image_path?: string;
  location_latitude?: number;
  location_longitude?: number;
  source?: string;
  ocr_metadata?: any;
  sync_status?: string;
  created_at: string;
  created_by?: string;
  fleet_vehicles?: { plate: string; make: string; model: string };
  fleet_providers?: { name: string };
  fleet_drivers?: { name: string };
}

export default function FleetFuel() {
  const { profile, companies, activeCompany, hasCompanyRole } = useAuth();
  const { canCreate, canEdit, canDelete, isViewer } = useFleetPermissions();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isFormSheetOpen, setIsFormSheetOpen] = useState(false);
  const [editingFuelLog, setEditingFuelLog] = useState<FuelLog | null>(null);
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [viewingLog, setViewingLog] = useState<FuelLog | null>(null);
  const [imageUrls, setImageUrls] = useState<{
    odometer?: string;
    pump?: string;
    invoice?: string;
  }>({});
  const [formData, setFormData] = useState({
    vehicle_id: "",
    occurred_at: "",
    liters: "",
    price_unit: "",
    total_value: "",
    odometer: "",
    fuel_type: "gasoline",
    station_id: "",
  });

  const currentCompanyId = activeCompany?.id || companies?.[0]?.id;

  // Fetch fuel logs
  const { data: fuelLogs, isLoading } = useQuery({
    queryKey: ["fleetFuelLogs", currentCompanyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fleet_fuel_logs")
        .select(`
          *,
          fleet_vehicles!vehicle_id(plate, make, model),
          fleet_providers!station_id(name),
          fleet_drivers!driver_id(name)
        `)
        .eq("company_id", currentCompanyId)
        .in("sync_status", ["reviewed", "synced"])
        .order("occurred_at", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!currentCompanyId,
  });

  // Fetch pending fuel logs count
  const { data: pendingCount = 0 } = useQuery({
    queryKey: ["fleetFuelLogsPendingCount", currentCompanyId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("fleet_fuel_logs")
        .select("*", { count: 'exact', head: true })
        .eq("company_id", currentCompanyId)
        .eq("sync_status", "pending");
      
      if (error) throw error;
      return count || 0;
    },
    enabled: !!currentCompanyId,
    refetchInterval: 30000, // Atualizar a cada 30 segundos
  });

  // Fetch fuel stations
  const { data: fuelStations } = useQuery({
    queryKey: ["fuelStations", currentCompanyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fleet_providers")
        .select("id, name, contact_info")
        .eq("company_id", currentCompanyId)
        .eq("type", "gas_station")
        .eq("status", "active")
        .order("name");
      
      if (error) throw error;
      return data;
    },
    enabled: !!currentCompanyId,
  });

  // Fetch vehicles for dropdown
  const { data: vehicles } = useQuery({
    queryKey: ["fleetVehicles", currentCompanyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fleet_vehicles")
        .select("id, plate, make, model")
        .eq("company_id", currentCompanyId)
        .eq("status", "active");

      if (error) throw error;
      return data;
    },
    enabled: !!currentCompanyId,
  });

  // Upload invoice function
  const uploadInvoice = async (file: File, fuelLogId: string): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${currentCompanyId}/fuel-invoices/${fuelLogId}.${fileExt}`;
    
    const { error: uploadError } = await supabase.storage
      .from('fleet-documents')
      .upload(fileName, file, {
        upsert: true,
        contentType: file.type
      });
    
    if (uploadError) throw uploadError;
    
    return fileName;
  };

  // Create fuel log mutation
  const createFuelLogMutation = useMutation({
    mutationFn: async (data: any) => {
      // 1. Create fuel log record
      const { data: fuelLog, error } = await supabase
        .from("fleet_fuel_logs")
        .insert([{ 
          ...data, 
          company_id: currentCompanyId, 
          created_by: profile?.id,
          station_id: data.station_id || null
        }])
        .select()
        .single();

      if (error) throw error;
      
      // 2. Upload invoice if exists
      let invoicePath: string | null = null;
      if (invoiceFile) {
        invoicePath = await uploadInvoice(invoiceFile, fuelLog.id);
        
        // 3. Update with invoice path
        const { error: updateError } = await supabase
          .from("fleet_fuel_logs")
          .update({ invoice_path: invoicePath })
          .eq("id", fuelLog.id);
        
        if (updateError) throw updateError;
      }
      
      return fuelLog;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fleetFuelLogs"] });
      setIsFormSheetOpen(false);
      resetForm();
      toast({
        title: "Sucesso",
        description: "Abastecimento registrado com sucesso.",
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Erro ao registrar abastecimento.",
      });
    },
  });

  // Update fuel log mutation
  const updateFuelLogMutation = useMutation({
    mutationFn: async ({ id, ...data }: any) => {
      // 1. Handle new file upload
      let newInvoicePath = editingFuelLog?.invoice_path;
      if (invoiceFile) {
        // 1.1 Delete old file if exists
        if (editingFuelLog?.invoice_path) {
          await supabase.storage
            .from('fleet-documents')
            .remove([editingFuelLog.invoice_path]);
        }
        
        // 1.2 Upload new file
        newInvoicePath = await uploadInvoice(invoiceFile, id);
      }
      
      // 2. Update record
      const { error } = await supabase
        .from("fleet_fuel_logs")
        .update({ 
          ...data, 
          invoice_path: newInvoicePath,
          station_id: data.station_id || null,
          updated_by: profile?.id, 
          updated_at: new Date().toISOString() 
        })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fleetFuelLogs"] });
      setIsFormSheetOpen(false);
      setEditingFuelLog(null);
      resetForm();
      toast({
        title: "Sucesso",
        description: "Abastecimento atualizado com sucesso.",
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Erro ao atualizar abastecimento.",
      });
    },
  });

  // Delete fuel log mutation
  const deleteFuelLogMutation = useMutation({
    mutationFn: async (id: string) => {
      // Check permission
      if (!hasCompanyRole(activeCompany?.id, 'company_admin')) {
        throw new Error('Você não tem permissão para excluir registros de combustível. Apenas administradores da empresa podem realizar esta ação.');
      }

      // Get record to find invoice_path
      const { data: fuelLog } = await supabase
        .from("fleet_fuel_logs")
        .select("invoice_path")
        .eq("id", id)
        .single();
      
      // Delete file if exists
      if (fuelLog?.invoice_path) {
        await supabase.storage
          .from('fleet-documents')
          .remove([fuelLog.invoice_path]);
      }

      const { error, count } = await supabase
        .from("fleet_fuel_logs")
        .delete()
        .eq("id", id);

      if (error) throw error;
      
      if (count === 0) {
        throw new Error('Registro de combustível não encontrado ou já foi removido.');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fleetFuelLogs"] });
      toast({
        title: "Sucesso",
        description: "Abastecimento removido com sucesso.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao remover abastecimento",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const resetForm = () => {
    setFormData({
      vehicle_id: "",
      occurred_at: "",
      liters: "",
      price_unit: "",
      total_value: "",
      odometer: "",
      fuel_type: "gasoline",
      station_id: "",
    });
    setInvoiceFile(null);
  };

  const handleSubmit = (data: FuelLogFormData) => {
    const liters = parseFloat(data.liters);
    const totalValue = data.total_value ? parseFloat(data.total_value) : null;
    const priceUnit = data.price_unit ? parseFloat(data.price_unit) : null;
    
    // Calcular price_unit a partir de total_value e liters se não fornecido
    const calculatedPriceUnit = !priceUnit && totalValue && liters > 0
      ? totalValue / liters
      : priceUnit;
    
    const submitData = {
      ...data,
      liters,
      price_unit: calculatedPriceUnit,
      total_value: totalValue,
      odometer: data.odometer ? parseFloat(data.odometer) : null,
      occurred_at: new Date(data.occurred_at).toISOString(),
    };
    
    if (editingFuelLog) {
      updateFuelLogMutation.mutate({ id: editingFuelLog.id, ...submitData });
    } else {
      createFuelLogMutation.mutate(submitData);
    }
  };

  const handleEdit = (fuelLog: FuelLog) => {
    setEditingFuelLog(fuelLog);
    setFormData({
      vehicle_id: fuelLog.vehicle_id,
      occurred_at: fuelLog.occurred_at.split('T')[0] + 'T' + fuelLog.occurred_at.split('T')[1]?.substring(0, 5),
      liters: fuelLog.liters.toString(),
      price_unit: fuelLog.price_unit?.toString() || "",
      total_value: fuelLog.total_value?.toString() || "",
      odometer: fuelLog.odometer?.toString() || "",
      fuel_type: fuelLog.fuel_type,
      station_id: fuelLog.station_id || "",
    });
    setInvoiceFile(null);
    setIsFormSheetOpen(true);
  };

  const getFuelTypeBadge = (fuelType: string) => {
    const fuelTypeLabels: Record<string, { label: string; variant: "default" | "secondary" | "outline" }> = {
      'gasoline': { label: 'Gasolina Comum', variant: 'default' },
      'gasoline_premium': { label: 'Gasolina Aditivada', variant: 'default' },
      'ethanol': { label: 'Etanol', variant: 'secondary' },
      'diesel': { label: 'Diesel Comum', variant: 'outline' },
      'diesel_s10': { label: 'Diesel S10', variant: 'outline' },
      'gnv': { label: 'GNV', variant: 'secondary' },
      'flex': { label: 'Flex', variant: 'default' }
    };
    
    const config = fuelTypeLabels[fuelType] || { label: fuelType, variant: 'outline' as const };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString("pt-BR");
  };

  const formatCurrency = (value: number | null) => {
    if (!value) return "N/A";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const viewImage = async (imagePath: string, title: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('fleet-images')
        .createSignedUrl(imagePath, 3600);
      
      if (error) throw error;
      
      if (data?.signedUrl) {
        window.open(data.signedUrl, '_blank');
      }
    } catch (error) {
      toast({
        title: "Erro ao abrir imagem",
        description: "Não foi possível carregar a imagem.",
        variant: "destructive"
      });
    }
  };

  const openLocationOnMap = (lat: number, lng: number) => {
    window.open(
      `https://www.google.com/maps?q=${lat},${lng}`,
      '_blank'
    );
  };

  const loadImageUrls = async (log: FuelLog) => {
    const urls: any = {};
    
    if (log.odometer_image_path) {
      const { data } = await supabase.storage
        .from('fleet-images')
        .createSignedUrl(log.odometer_image_path, 3600);
      if (data?.signedUrl) urls.odometer = data.signedUrl;
    }
    
    if (log.pump_image_path) {
      const { data } = await supabase.storage
        .from('fleet-images')
        .createSignedUrl(log.pump_image_path, 3600);
      if (data?.signedUrl) urls.pump = data.signedUrl;
    }
    
    if (log.invoice_image_path) {
      const { data } = await supabase.storage
        .from('fleet-images')
        .createSignedUrl(log.invoice_image_path, 3600);
      if (data?.signedUrl) urls.invoice = data.signedUrl;
    }
    
    setImageUrls(urls);
  };

  const columns: ColumnDef<FuelLog>[] = [
    {
      accessorKey: "fleet_vehicles.plate",
      header: "Veículo",
      cell: ({ row }) => {
        const vehicle = row.original.fleet_vehicles;
        return vehicle?.plate || "N/A";
      },
    },
    {
      accessorKey: "fleet_drivers.name",
      header: "Motorista",
      cell: ({ row }) => {
        const driver = row.original.fleet_drivers;
        return driver?.name ? (
          <span className="text-sm">{driver.name}</span>
        ) : (
          <span className="text-muted-foreground text-xs">Admin</span>
        );
      },
    },
    {
      accessorKey: "fleet_providers.name",
      header: "Posto",
      cell: ({ row }) => {
        const provider = row.original.fleet_providers;
        return provider?.name ? (
          <span className="text-sm">{provider.name}</span>
        ) : (
          <span className="text-muted-foreground text-xs">N/A</span>
        );
      },
    },
    {
      accessorKey: "occurred_at",
      header: "Data/Hora",
      cell: ({ row }) => formatDateTime(row.getValue("occurred_at")),
    },
    {
      accessorKey: "liters",
      header: "Litros",
      cell: ({ row }) => `${row.getValue("liters")} L`,
    },
    {
      accessorKey: "price_unit",
      header: "Preço/Litro",
      cell: ({ row }) => formatCurrency(row.getValue("price_unit")),
    },
    {
      accessorKey: "fuel_type",
      header: "Combustível",
      cell: ({ row }) => getFuelTypeBadge(row.getValue("fuel_type")),
    },
    {
      accessorKey: "odometer",
      header: "Odômetro",
      cell: ({ row }) => {
        const odometer = row.getValue("odometer") as number;
        return odometer ? `${odometer} km` : "N/A";
      },
    },
    {
      id: "total",
      header: "Total",
      cell: ({ row }) => {
        const totalValue = row.original.total_value;
        if (totalValue) return formatCurrency(totalValue);
        
        const liters = row.original.liters;
        const priceUnit = row.original.price_unit;
        if (!priceUnit) return "N/A";
        return formatCurrency(liters * priceUnit);
      },
    },
    {
      id: "location",
      header: "Localização",
      cell: ({ row }) => {
        const { location_latitude, location_longitude } = row.original;
        
        if (!location_latitude || !location_longitude) {
          return <span className="text-muted-foreground text-xs">N/A</span>;
        }
        
        return (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openLocationOnMap(location_latitude, location_longitude)}
            title={`${location_latitude.toFixed(6)}, ${location_longitude.toFixed(6)}`}
          >
            <MapPin className="h-3 w-3 mr-1" />
            Ver mapa
          </Button>
        );
      },
    },
    {
      id: "source",
      header: "Origem",
      cell: ({ row }) => {
        const source = row.original.source;
        const hasDriver = !!row.original.driver_id;
        const ocrMetadata = row.original.ocr_metadata;
        
        if (source === 'mobile' || source === 'driver_app') {
          return (
            <div className="flex flex-col gap-1">
              <Badge variant="secondary" className="w-fit">
                <Smartphone className="h-3 w-3 mr-1" />
                FleetONE Driver
              </Badge>
              {ocrMetadata?.invoice_confidence && (
                <span className="text-xs text-muted-foreground">
                  OCR: {ocrMetadata.invoice_confidence.toFixed(0)}%
                </span>
              )}
            </div>
          );
        }
        
        if (!hasDriver && row.original.created_by) {
          return <Badge variant="outline">💻 Admin</Badge>;
        }
        
        return <Badge variant="outline">📥 Sistema</Badge>;
      },
    },
    {
      id: "invoice",
      header: "Nota Fiscal",
      cell: ({ row }) => {
        const invoicePath = row.original.invoice_path;
        if (!invoicePath) {
          return <span className="text-muted-foreground text-xs">Sem nota</span>;
        }
        
        return (
          <Button
            variant="ghost"
            size="sm"
            onClick={async () => {
              const { data } = await supabase.storage
                .from('fleet-documents')
                .createSignedUrl(invoicePath, 3600);
              
              if (data?.signedUrl) {
                window.open(data.signedUrl, '_blank');
              }
            }}
          >
            <FileText className="h-4 w-4 mr-1" />
            Ver nota
          </Button>
        );
      },
    },
    {
      id: "actions",
      header: "Ações",
      cell: ({ row }) => {
        const log = row.original;
        const isDriverLog = log.source === 'driver_app';
        const canEditThis = isDriverLog 
          ? hasCompanyRole(activeCompany?.id, 'company_admin') 
          : canEdit;
        
        return (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setViewingLog(log);
                loadImageUrls(log);
              }}
              title="Ver detalhes completos"
            >
              <Eye className="h-4 w-4" />
            </Button>
            
            {canEditThis && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleEdit(log)}
              >
                <Edit2 className="h-4 w-4" />
              </Button>
            )}
            
            {canDelete && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => deleteFuelLogMutation.mutate(log.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  if (!currentCompanyId) {
    return (
      <div className="p-6">
        <p>Nenhuma empresa associada encontrada.</p>
      </div>
    );
  }

  const totalLiters = fuelLogs?.reduce((sum, log) => sum + log.liters, 0) || 0;
  const totalCost = fuelLogs?.reduce((sum, log) => {
    if (log.total_value) return sum + log.total_value;
    return sum + (log.liters * (log.price_unit || 0));
  }, 0) || 0;
  const averagePrice = fuelLogs?.length ? 
    fuelLogs.reduce((sum, log) => sum + (log.price_unit || 0), 0) / fuelLogs.filter(log => log.price_unit).length : 0;
  const driverAppLogs = fuelLogs?.filter(log => log.source === 'driver_app').length || 0;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Combustível</h1>
          <p className="text-muted-foreground">Gerencie abastecimentos e consumo da frota</p>
        </div>
        <div className="flex gap-2 items-center">
          {isViewer && (
            <Badge variant="secondary">
              👁️ Modo Visualização
            </Badge>
          )}
          <Button 
            disabled={!canCreate} 
            onClick={() => { 
              setEditingFuelLog(null); 
              resetForm(); 
              setIsFormSheetOpen(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Novo Abastecimento
          </Button>

          <FuelLogFormSheet
            open={isFormSheetOpen}
            onOpenChange={setIsFormSheetOpen}
            fuelLog={editingFuelLog}
            onSubmit={handleSubmit}
            isLoading={createFuelLogMutation.isPending || updateFuelLogMutation.isPending}
            vehicles={vehicles}
            stations={fuelStations}
            invoiceFile={invoiceFile}
            setInvoiceFile={setInvoiceFile}
          />
        </div>
      </div>

      <Tabs defaultValue="registros" className="space-y-6">
        <TabsList>
          <TabsTrigger value="registros">Registros</TabsTrigger>
          <TabsTrigger value="revisao">
            Pendentes de Revisão
            {pendingCount > 0 && (
              <Badge className="ml-2" variant="destructive">{pendingCount}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="registros" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Abastecimentos</CardTitle>
            <Fuel className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{fuelLogs?.length || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Litros</CardTitle>
            <Fuel className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalLiters.toFixed(2)} L</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Custo Total</CardTitle>
            <Calculator className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalCost)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Preço Médio/Litro</CardTitle>
            <Calculator className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(averagePrice)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Via App Motorista</CardTitle>
            <Smartphone className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{driverAppLogs}</div>
            <p className="text-xs text-muted-foreground">
              {((driverAppLogs / (fuelLogs?.length || 1)) * 100).toFixed(0)}% do total
            </p>
          </CardContent>
        </Card>
      </div>

          {/* Data Table */}
          <Card>
            <CardHeader>
              <CardTitle>Histórico de Abastecimentos</CardTitle>
              <CardDescription>Visualize e gerencie todos os registros de abastecimento</CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable columns={columns} data={fuelLogs || []} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="revisao">
          <FuelReview />
        </TabsContent>
      </Tabs>

      {/* Detailed View Modal */}
      <Dialog open={!!viewingLog} onOpenChange={() => {
        setViewingLog(null);
        setImageUrls({});
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhes do Abastecimento</DialogTitle>
            <DialogDescription>
              Informações completas sobre o registro de abastecimento
            </DialogDescription>
          </DialogHeader>
          
          {viewingLog && (
            <div className="space-y-6">
              {/* Badge de origem */}
              <div className="flex items-center justify-between">
                {(viewingLog.source === 'mobile' || viewingLog.source === 'driver_app') ? (
                  <Badge variant="secondary" className="text-sm">
                    <Smartphone className="h-3 w-3 mr-1" />
                    Registrado via FleetONE Driver
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-sm">
                    💻 Registrado por Admin
                  </Badge>
                )}
                
                {viewingLog.sync_status && (
                  <Badge variant={
                    viewingLog.sync_status === 'synced' ? 'default' :
                    viewingLog.sync_status === 'reviewed' ? 'secondary' :
                    'outline'
                  }>
                    {viewingLog.sync_status === 'synced' && '✓ Sincronizado'}
                    {viewingLog.sync_status === 'reviewed' && '✓ Revisado'}
                    {viewingLog.sync_status === 'pending' && '⏳ Pendente'}
                  </Badge>
                )}
              </div>

              {/* Dados principais em grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-muted-foreground">Veículo</Label>
                  <p className="font-medium">
                    {viewingLog.fleet_vehicles?.plate} - {viewingLog.fleet_vehicles?.make} {viewingLog.fleet_vehicles?.model}
                  </p>
                </div>
                
                {viewingLog.fleet_drivers && (
                  <div>
                    <Label className="text-muted-foreground">Motorista</Label>
                    <p className="font-medium">{viewingLog.fleet_drivers.name}</p>
                  </div>
                )}
                
                <div>
                  <Label className="text-muted-foreground">Data/Hora</Label>
                  <p className="font-medium">{formatDateTime(viewingLog.occurred_at)}</p>
                </div>
                
                <div>
                  <Label className="text-muted-foreground">Hodômetro</Label>
                  <p className="font-medium">{viewingLog.odometer ? `${viewingLog.odometer} km` : 'N/A'}</p>
                </div>
                
                <div>
                  <Label className="text-muted-foreground">Litros</Label>
                  <p className="font-medium">{viewingLog.liters} L</p>
                </div>
                
                <div>
                  <Label className="text-muted-foreground">Tipo de Combustível</Label>
                  <div className="mt-1">{getFuelTypeBadge(viewingLog.fuel_type)}</div>
                </div>
                
                <div>
                  <Label className="text-muted-foreground">Preço/Litro</Label>
                  <p className="font-medium">{formatCurrency(viewingLog.price_unit)}</p>
                </div>
                
                <div>
                  <Label className="text-muted-foreground">Valor Total</Label>
                  <p className="font-medium text-lg">
                    {viewingLog.total_value 
                      ? formatCurrency(viewingLog.total_value)
                      : formatCurrency((viewingLog.price_unit || 0) * viewingLog.liters)
                    }
                  </p>
                </div>
                
                {viewingLog.fleet_providers && (
                  <div>
                    <Label className="text-muted-foreground">Posto</Label>
                    <p className="font-medium">{viewingLog.fleet_providers.name}</p>
                  </div>
                )}
              </div>

              {/* Galeria de Fotos */}
              {(imageUrls.odometer || imageUrls.pump || imageUrls.invoice) && (
                <div className="border-t pt-4">
                  <Label className="text-lg font-semibold mb-3 block">Fotos do Abastecimento</Label>
                  <div className="grid grid-cols-3 gap-4">
                    {imageUrls.odometer && (
                      <div className="space-y-2">
                        <img 
                          src={imageUrls.odometer}
                          alt="Hodômetro" 
                          className="w-full h-48 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition"
                          onClick={() => window.open(imageUrls.odometer, '_blank')}
                        />
                        <p className="text-xs text-center text-muted-foreground font-medium">Hodômetro</p>
                      </div>
                    )}
                    
                    {imageUrls.pump && (
                      <div className="space-y-2">
                        <img 
                          src={imageUrls.pump}
                          alt="Bomba de Combustível" 
                          className="w-full h-48 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition"
                          onClick={() => window.open(imageUrls.pump, '_blank')}
                        />
                        <p className="text-xs text-center text-muted-foreground font-medium">Bomba</p>
                      </div>
                    )}
                    
                    {imageUrls.invoice && (
                      <div className="space-y-2">
                        <img 
                          src={imageUrls.invoice}
                          alt="Nota Fiscal" 
                          className="w-full h-48 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition"
                          onClick={() => window.open(imageUrls.invoice, '_blank')}
                        />
                        <p className="text-xs text-center text-muted-foreground font-medium">Nota Fiscal</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Localização */}
              {viewingLog.location_latitude && viewingLog.location_longitude && (
                <div className="border-t pt-4">
                  <Label className="text-lg font-semibold mb-3 block">Localização do Abastecimento</Label>
                  <div className="space-y-3">
                    <LocationDisplay 
                      companyId={currentCompanyId}
                      latitude={viewingLog.location_latitude}
                      longitude={viewingLog.location_longitude}
                    />
                    <Button
                      variant="outline"
                      onClick={() => openLocationOnMap(viewingLog.location_latitude!, viewingLog.location_longitude!)}
                    >
                      <MapPin className="h-4 w-4 mr-2" />
                      Abrir no Google Maps
                    </Button>
                  </div>
                </div>
              )}

              {/* Metadados OCR (se disponível) */}
              {viewingLog.ocr_metadata && (
                <div className="border-t pt-4">
                  <Label className="text-lg font-semibold mb-3 block">Dados do OCR</Label>
                  <div className="bg-muted p-4 rounded-lg space-y-2 text-sm">
                    {viewingLog.ocr_metadata.ocr_version && (
                      <p><span className="font-medium">Versão OCR:</span> {viewingLog.ocr_metadata.ocr_version}</p>
                    )}
                    
                    {viewingLog.ocr_metadata.station_cnpj && (
                      <p><span className="font-medium">CNPJ do Posto:</span> {viewingLog.ocr_metadata.station_cnpj}</p>
                    )}
                    
                    {viewingLog.ocr_metadata.detected_fuel_type && (
                      <p><span className="font-medium">Tipo Detectado:</span> {viewingLog.ocr_metadata.detected_fuel_type}</p>
                    )}
                    
                    {viewingLog.ocr_metadata.invoice_confidence && (
                      <p>
                        <span className="font-medium">Confiança (Nota):</span> {viewingLog.ocr_metadata.invoice_confidence}%
                        {viewingLog.ocr_metadata.invoice_confidence < 70 && (
                          <Badge variant="secondary" className="ml-2">Baixa confiança</Badge>
                        )}
                      </p>
                    )}
                    
                    {viewingLog.ocr_metadata.odometer_confidence && (
                      <p>
                        <span className="font-medium">Confiança (Odômetro):</span> {viewingLog.ocr_metadata.odometer_confidence}%
                      </p>
                    )}
                    
                    {viewingLog.ocr_metadata.calculation_method && (
                      <p>
                        <span className="font-medium">Método de Cálculo:</span> {viewingLog.ocr_metadata.calculation_method}
                      </p>
                    )}
                    
                    {viewingLog.ocr_metadata.raw_ocr_texts && (
                      <details className="mt-3">
                        <summary className="cursor-pointer font-medium">Textos Brutos (OCR)</summary>
                        <div className="mt-2 space-y-2 text-xs bg-background p-2 rounded border">
                          {viewingLog.ocr_metadata.raw_ocr_texts.invoice && (
                            <div>
                              <p className="font-semibold">Nota Fiscal:</p>
                              <pre className="whitespace-pre-wrap mt-1">{viewingLog.ocr_metadata.raw_ocr_texts.invoice}</pre>
                            </div>
                          )}
                          {viewingLog.ocr_metadata.raw_ocr_texts.odometer && (
                            <div>
                              <p className="font-semibold mt-2">Odômetro:</p>
                              <pre className="whitespace-pre-wrap mt-1">{viewingLog.ocr_metadata.raw_ocr_texts.odometer}</pre>
                            </div>
                          )}
                        </div>
                      </details>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
